/**
 * Outbound-email test mode. When enabled, EVERY outgoing email — approval requests,
 * reminders, escalations, submitter updates, final distribution, everything that
 * funnels through functions/src/mail/mailQueue.js's enqueueMail() — is redirected to
 * one test address instead of its real recipients, so the team can exercise the full
 * approval/notification flow without spamming real staff/attorneys/clients. See
 * functions/src/mail/testMode.js for where the redirect actually happens.
 *
 * Routed through the narrow `adminUpdateTestMode` callable rather than the generic
 * adminUpdateSettings used by AdminNumbering etc., because this shape needs real
 * validation (test mode can't be enabled without a valid test email) — see
 * functions/src/admin/adminActions.js's updateTestMode.
 */

import { useEffect, useState } from 'react';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useDoc } from '../../lib/useFirestore';

const field = 'w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm';

export default function AdminTestMode() {
  const { data: stored, loading } = useDoc('settings/testMode');
  const { run, busy, error, saved } = useCallable();
  const [settings, setSettings] = useState(null);
  // Tracks whether the admin has made local edits since the last time `settings` was
  // synced from `stored`. Without this, `settings` would only ever be seeded once and
  // would go stale the moment Firestore's doc changes again (another tab/session, or
  // this component's own save round-tripping through onSnapshot) — see the bug this
  // fixes: the banner in Shell.jsx reads `stored` directly and is always live, while
  // this form used to freeze on whatever `stored` looked like at first mount.
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) {
      setSettings(stored ? structuredClone(stored) : { enabled: false, testEmail: '' });
    }
  }, [stored, dirty]);

  if (loading || !settings) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;

  const patch = (key, value) => {
    setDirty(true);
    setSettings({ ...settings, [key]: value });
  };
  const save = async () => {
    const result = await run('adminUpdateTestMode', {
      enabled: settings.enabled,
      testEmail: settings.testEmail || '',
    });
    if (!result) return; // stop on failure so the error stays visible and edits aren't lost
    setDirty(false); // let the next Firestore snapshot (our own write) resync the display
  };

  return (
    <div className="space-y-4">
      {settings.enabled && (
        <div className="rounded border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950 px-3 py-2 text-sm font-medium text-amber-900 dark:text-amber-100">
          TEST MODE ON — all email is being redirected to{' '}
          <strong>{settings.testEmail || '(no address set)'}</strong>.
        </div>
      )}

      <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Test mode</h2>
          <CallStatus busy={busy} error={error} saved={saved} />
        </div>

        <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
          When enabled, ALL outgoing email (approval requests, reminders, escalations,
          final distribution, everything) is redirected to the test address below
          instead of real recipients.
        </p>

        <div className="mt-3 space-y-2">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={Boolean(settings.enabled)}
              onChange={(e) => patch('enabled', e.target.checked)}
            />
            Enabled — redirect all outbound email to the test address
          </label>

          <label className="block text-xs text-slate-600 dark:text-slate-300">
            Test email address
            <input
              className={`${field} mt-1`}
              type="email"
              value={settings.testEmail || ''}
              onChange={(e) => patch('testEmail', e.target.value)}
              placeholder="jason@example.com"
            />
          </label>
        </div>

        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="mt-4 rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          Save test mode settings
        </button>
      </section>
    </div>
  );
}
